interface SparklineProps {
  data: readonly number[]
  color: string
  width?: number
  height?: number
  /** Draw a dotted reference line at this value (usually the opening print). */
  baseline?: number
  fill?: boolean
  strokeWidth?: number
}

/** Deliberately unlabelled: shape only, the number beside it carries the value. */
export function Sparkline({
  data,
  color,
  width = 92,
  height = 26,
  baseline,
  fill = true,
  strokeWidth = 1.5,
}: SparklineProps) {
  if (data.length < 2) return <svg width={width} height={height} aria-hidden="true" />

  let min = Infinity
  let max = -Infinity
  for (const v of data) {
    if (v < min) min = v
    if (v > max) max = v
  }
  if (baseline !== undefined) {
    min = Math.min(min, baseline)
    max = Math.max(max, baseline)
  }
  // Nothing has moved yet: draw the line, skip the area, or a fresh round
  // renders as a solid block of colour.
  const flat = max === min
  const pad = (max - min) * 0.12 || Math.abs(max) * 0.02 || 1
  min -= pad
  max += pad

  const x = (i: number) => (i / (data.length - 1)) * width
  const y = (v: number) => height - ((v - min) / (max - min)) * height

  const line = data.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)},${y(v).toFixed(2)}`).join('')
  const area = `${line}L${width},${height}L0,${height}Z`
  const id = `sp${Math.round(x(data.length - 1))}${data.length}${color.replace(/\W/g, '')}`

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      {fill && !flat ? (
        <>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.26" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${id})`} />
        </>
      ) : null}
      {baseline !== undefined ? (
        <line
          x1="0"
          x2={width}
          y1={y(baseline)}
          y2={y(baseline)}
          stroke="currentColor"
          strokeOpacity="0.35"
          strokeDasharray="2 3"
          strokeWidth="1"
        />
      ) : null}
      <path d={line} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" />
    </svg>
  )
}
